/* eslint-disable no-unused-vars */
import React, { useState, useEffect, Fragment } from 'react'
import './index.scss'
import { Modal, Divider, Form, Input, Button, message, Checkbox } from 'antd'
import { QrcodeOutlined, MobileOutlined, MailOutlined, InfoCircleFilled } from '@ant-design/icons'
import { verifyTotpAuthCode, getCode, verifyCode, queryUserMfaConfig, queryEnterpriseMfaConfig } from '@/services/mfa'
import VerifyCode from '../VerifyCode'
import intl from 'react-intl-universal'
import { PATTERN, INPUT_TIP, MFA_TYPE, MFA_VERIFY_TYPE } from '@/config/default'
import { Encrypt } from '@/utils/aes'
import { px2rem } from '@/utils/utils'
import user from '@/utils/user'

const ITEM_LABEL = {
  MOBILE: intl.get('mfa.phoneNumber'),
  EMAIL: intl.get('mfa.emailAddress')
}

const MFA_ICON = {
  TOTP: <QrcodeOutlined />,
  MOBILE: <MobileOutlined />,
  EMAIL: <MailOutlined />
}

function MFAModal ({ visible, onConfirm, onCancel }) {
  const [form] = Form.useForm()
  const [mfaType, setMfaType] = useState('')
  const [count, setCount] = useState(0)
  const [mfaConfig, setMfaConfig] = useState({
    TOTP: null,
    MOBILE: null,
    EMAIL: null
  })
  const [enterpriseMFAConfig, setEnterpriseMFAConfig] = useState({})

  useEffect(() => {
    if (visible) {
      getEnterpriseMFAConfig()
      getUserMFAConfig()
    }
  }, [visible])

  /** 获取企业MFA配置 */
  const getEnterpriseMFAConfig = async () => {
    try {
      const res = await queryEnterpriseMfaConfig()
      setEnterpriseMFAConfig(res)
    } catch (error) {
      console.log('获取企业MFA配置失败：', error)
    }
  }

  /** 获取用户绑定的MFA方式 */
  const getUserMFAConfig = async () => {
    try {
      const config = await queryUserMfaConfig()
      if (config.length > 0) {
        const defaultConfig = { TOTP: null, MOBILE: null, EMAIL: null }
        const newConfig = Object.keys(defaultConfig).reduce((obj, key) => {
          const item = config.find(item => item.type === key)
          if (item) {
            obj[key] = item
          }
          return obj
        }, {})
        const type = Object.keys(newConfig).find(key => newConfig[key])
        setMfaConfig(newConfig)
        setMfaType(type)
      }
    } catch (error) {
      console.log('获取用户MFA配置失败：', error)
    }
  }

  /** 切换MFA方式 */
  const handleMFAType = (type) => {
    form.resetFields()
    setMfaType(type)
  }

  /** 勾选30天免验证登录时生成随机数, 并保存到localStorage，防止不同设备但是同一电脑配置同一账号登录 */
  const handleCheck = (e) => {
    const { checked } = e.target
    console.log(222, form.getFieldsValue())
    const randomNum = Math.floor(Math.random() * 1000000)
    const userData = user.getStagingUserInfo() || {}
    const { account, enterprise: { abbrName } } = userData
    const key = `${abbrName}_${account}`
    const validDaysMap = JSON.parse(localStorage.getItem('react_mfa_valid_days')) || {}
    if (checked) {
      // 最多保存20个账号的免验证登录, 超过20个账号后删除第一个
      if (Object.keys(validDaysMap).length >= 20) {
        const firstKey = Object.keys(validDaysMap)[0]
        delete validDaysMap[firstKey]
      }
      validDaysMap[key] = randomNum
      localStorage.setItem('react_mfa_valid_days', JSON.stringify(validDaysMap))
    } else {
      delete validDaysMap[key]
      localStorage.setItem('react_mfa_valid_days', JSON.stringify(validDaysMap))
    }
  }

  /** 处理提交验证 */
  const handleSubmit = async (e) => {
    e.preventDefault()
    const userData = user.getStagingUserInfo() || {}
    const { account, enterprise: { abbrName } } = userData
    const deviceFingerprint = localStorage.getItem('react_device_fingerprint')
    const validDaysMap = JSON.parse(localStorage.getItem('react_mfa_valid_days'))
    const key = `${abbrName}_${account}`
    const validRandomNum = validDaysMap ? validDaysMap[key] || '' : ''
    try {
      const info = await form.validateFields()
      const { code, mfaValidDays = false } = info || {}
      if (mfaType === 'TOTP') {
        const params = {
          code,
          deviceFingerprint: Encrypt(deviceFingerprint + validRandomNum),
          mfaValidDays
        }
        await verifyTotpAuthCode(params)
      } else {
        const params = {
          type: mfaType,
          code,
          deviceFingerprint: Encrypt(deviceFingerprint + validRandomNum),
          mfaValidDays
        }
        await verifyCode(params)
      }
      onConfirm()
      message.success(intl.get('mfa.verificationSuccessful'))
    } catch (error) {
      console.log('验证失败：', error)
    }
  }

  /** 处理取消 */
  const handleCancel = () => {
    form.resetFields()
    setMfaType('')
    setCount(0)
    setMfaConfig({
      TOTP: null,
      MOBILE: null,
      EMAIL: null
    })
    setEnterpriseMFAConfig({})
    onCancel()
  }

  const userData = user.getStagingUserInfo() || {}
  const config = Object.keys(mfaConfig).filter(key => mfaConfig[key]) || []
  return (
    <Modal className='MFA-login'
      width={px2rem(520)}
      open={visible}
      onCancel={handleCancel}
      mask={{
        closable: false
      }}
      footer={null}
      centered
    >
      <div className='MFA-login-tips'>
        <InfoCircleFilled />
        <span>{intl.get('mfa.loginVerificationPrompt', { account: userData.account })}</span>
      </div>
      {
        config && config.length
          ? (
            <div className='MFA-login-content'>
              <div className='MFA-breadcrumb'>
                {
                  config.map((key, index) => (
                    <Fragment key={key}>
                      <span className={'MFA-tab-item' + (mfaType === key ? ' active' : '')} onClick={() => handleMFAType(key)}>
                        {MFA_ICON[key]}
                        {MFA_TYPE[key]}
                      </span>
                      {index === config.length - 1 ? null : <Divider type='vertical' />}
                    </Fragment>
                  ))
                }
              </div>
              <div className='MFA-login-form'>
                {mfaType === 'TOTP' && (
                  <Form layout='vertical' labelAlign='left' requiredMark={false} colon={false} form={form}>
                    <Form.Item label={intl.get('mfa.username')}>
                      <div className='MFA-qrcode'>{userData.account}</div>
                    </Form.Item>
                    <Form.Item label={intl.get('mfa.securityCode')}
                      name="code"
                      rules={[
                        { required: true, message: INPUT_TIP.CODE_EMPTY },
                        { pattern: PATTERN.CODE, message: INPUT_TIP.CODE_ERROR }
                      ]}
                    >
                      <Input size='large' type='text' placeholder={INPUT_TIP.TOTP} allowClear />
                    </Form.Item>
                    <Form.Item>
                      <Button className='submit-mfa-btn' size='large' type='primary' onClick={handleSubmit}>{intl.get('mfa.submitVerification')}</Button>
                    </Form.Item>
                    {
                      enterpriseMFAConfig.mfaValidDays && (
                        <Form.Item
                          name="mfaValidDays"
                          valuePropName="checked"
                        >
                          <Checkbox onChange={handleCheck}>{intl.get('mfa.verificationFreeLogin')}</Checkbox>
                        </Form.Item>
                      )
                    }
                  </Form>
                )}
                {(mfaType === 'MOBILE' || mfaType === 'EMAIL') && (
                  <Form labelAlign='left' colon={false} form={form} requiredMark={false} >
                    <Form.Item label={ITEM_LABEL[mfaType]}><span>{mfaConfig[mfaType] ? mfaConfig[mfaType][mfaType.toLocaleLowerCase()] : '--'}</span></Form.Item>
                    <Form.Item label={intl.get('mfa.captcha')}
                      name="code"
                      rules={[
                        { required: true, message: INPUT_TIP.CODE_EMPTY },
                        { pattern: PATTERN.CODE, message: INPUT_TIP.CODE_ERROR }
                      ]}
                    >
                      <VerifyCode form={form} noAccount type={mfaType} />
                    </Form.Item>
                    <Form.Item>
                      <Button className='submit-mfa-btn' onClick={handleSubmit} type='primary' htmlType='submit'>{intl.get('mfa.submitVerification')}</Button>
                    </Form.Item>
                    {
                      enterpriseMFAConfig.mfaValidDays && (
                        <Form.Item
                          name="mfaValidDays"
                          valuePropName="checked"
                        >
                          <Checkbox onChange={handleCheck}>{intl.get('mfa.verificationFreeLogin')}</Checkbox>
                        </Form.Item>
                      )
                    }
                  </Form>
                )}
              </div>
            </div>
          )
          : null
      }

    </Modal>
  )
}

export default MFAModal